import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';

import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Stack from 'react-bootstrap/Stack';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { MdOutlineInfo } from 'react-icons/md';
import { usePreferences } from '../../context/PreferencesContext';
import { NavBreadcrumb } from '../layout/NavBreadcrumb';
import { HouseAddressTitle } from '../data-input/HouseAddressTitle';
import { MainFormElement } from '../data-input/MainFormElement';
import { MainFormToggableElement } from '../data-input/MainFormToggableElement';
import { InputHeader } from '../layout/InputHeader';
import { LoadingMessage } from '../layout/LoadingMessage';
import { GeoAdminMapView } from '../data-input/maps/GeoAdminMapView';

import { selectDataAttrs, selectDataError, selectDataLoading } from '../../reducers/dataSelectors';

import { logMessage } from '../../lib/logging';

/* =========================================================
   Preference configuration
   ========================================================= */

const preferenceConfig = [
	{
		key: 'thermal',
		label: 'Thermischer Komfort',
		children: ['Raumtemperatur', 'Sonnen- und Blendschutz', 'Strahlung'],
	},
	{
		key: 'air',
		label: 'Raumluft-Qualität',
		children: ['Luftwechsel', 'Raumluftfeuchte', 'Schadstoffe', 'Geruchsvermeidung', 'Radon'],
	},
	{
		key: 'hotwater',
		label: 'Brauchwarmwasser',
		children: ['Solltemperatur', 'Warmhaltung', 'Hygiene', 'Kalk'],
	},
	{
		key: 'visual',
		label: 'Visueller Komfort',
		children: ['Tageslicht', 'Kunstlicht', 'Gestaltungsqualität', 'Privatsphäre'],
	},
	{
		key: 'acoustic',
		label: 'Akustischer Komfort',
		children: ['Raumakustik', 'Interne Lärmemissionen', 'Externe Lärmemissionen'],
	},
	{
		key: 'usage',
		label: 'Gebrauchs-/Aufenthaltsqualität',
		children: [
			'Identifikation',
			'Sicherheitsgefühl',
			'Soziale Interaktion',
			'Aneignung',
			'Gebäudeautomation',
			'Materialität',
			'Vegetation',
			'Nutzungsflexibilität',
			'Raumqualität',
		],
	},
];

const qualityTooltipMap = {
	Raumtemperatur:
		'Die Raumtemperatur beeinflusst die Oberflächentemperatur und damit das thermische Empfinden im Raum.',
	'Sonnen- und Blendschutz':
		'Die Beschattung reguliert die Sonneneinstrahlung und beeinflusst dadurch sowohl die Temperatur als auch die Blendung.',
	Strahlung:
		'Eine Reduktion der Strahlung vermindert Zuglufterscheinungen an kalten Oberflächen.',

	Luftwechsel:
		'Ein ausreichender Luftaustausch ist unabdingbar für ein gutes Raumklima mit Frischluft und einem geringen Anteil an CO2.',
	Raumluftfeuchte:
		'Das Raumklima und die Luftqualität werden durch eine angemessene Luftfeuchtigkeit wesentlich beeinflusst.',
	Schadstoffe:
		'Die Vermeidung und Reduktion von Schadstoffen ist zentral für die Gesundheit im Raum.',
	Geruchsvermeidung:
		'Neben der Geruchsvermeidung soll auch der Entstehung von Keimen vorgebeugt werden.',
	Radon: 'Ein erhöhter Radonwert kann insbesondere in Kellerräumen auftreten und gesundheitsgefährdend sein.',

	Solltemperatur:
		'Eine auf die Nutzenden abgestimmte Solltemperatur kann Energie sparen und ein angenehmes Raumklima schaffen.',
	Warmhaltung: 'Die Warmhaltung nach SIA 385 stellt hygienisches Brauchwarmwasser sicher.',
	Hygiene: 'Die Hygiene kann durch eine Legionellenschaltung verbessert werden.',
	Kalk: 'Das Vermeiden von Kalkbildung trägt zur Nutzerfreundlichkeit und Hygiene bei.',

	Tageslicht: 'Der Lichteinfall sollte der Nutzung angemessen sein.',
	Kunstlicht:
		'Die Beleuchtung unterstützt das natürliche Licht und trägt zu einer guten Atmosphäre bei.',
	Gestaltungsqualität: 'Die Qualität der Architektur beeinflusst die Aufenthaltsqualität.',
	Privatsphäre: 'Die Sicherstellung der Privatsphäre trägt zu einem angenehmen Wohnklima bei.',

	Raumakustik:
		'Eine gute Raumakustik ohne Nachhall trägt wesentlich zur Aufenthaltsqualität bei.',
	'Interne Lärmemissionen': 'Störende Betriebsgeräusche sollten minimiert werden.',
	'Externe Lärmemissionen': 'Das Gebäude soll wirksam vor Lärm von aussen schützen.',

	Identifikation:
		'Wenn sich die Bewohnenden wohlfühlen, können sie sich mit dem Wohnort identifizieren.',
	Sicherheitsgefühl:
		'Die individuell empfundene Sicherheit kann durch Orientierungshilfen erhöht werden.',
	'Soziale Interaktion': 'Begegnungszonen fördern den Austausch unter den Bewohnenden.',
	Aneignung: 'Das Individualisierungspotenzial beeinflusst das Wohlbefinden.',
	Gebäudeautomation: 'Eine gut steuerbare Gebäudeautomation führt zu weniger Reklamationen.',
	Materialität:
		'Materialien können durch ihre Beschaffenheit eine positive Raumstimmung erzeugen.',
	Vegetation: 'Eine naturbelassene Umgebung fördert den Naturbezug.',
	Nutzungsflexibilität: 'Flexible Raumnutzung ermöglicht individuelle Anpassungen.',
	Raumqualität: 'Proportionen und Struktur prägen Räume und Menschen.',
};

function buildInitialPreferences() {
	const state = {};
	preferenceConfig.forEach((section) => {
		state[section.key] = {
			expanded: true,
			checked: true,
			children: {},
		};
		section.children.forEach((child) => {
			state[section.key].children[child] = true;
		});
	});
	return state;
}

/* =========================================================
   Component
   ========================================================= */

export const DataEntry = () => {
	const navigate = useNavigate();
	const { preferences: storedPreferences, setPreferences: setStoredPreferences } =
		usePreferences();

	const [preferences, setLocalPreferences] = useState(
		storedPreferences ?? buildInitialPreferences()
	);

	useEffect(() => {
		if (!storedPreferences) {
			//console.log('[Preferences] Initializing default preferences');
			const initial = buildInitialPreferences();
			setStoredPreferences(initial);
			setLocalPreferences(initial);
		} else {
			//console.log('[Preferences] Hydrating from stored preferences');
			setLocalPreferences(storedPreferences);
		}
	}, [storedPreferences, setStoredPreferences]);

	const loading = useSelector(selectDataLoading);
	const attrs = useSelector(selectDataAttrs);
	const error = useSelector(selectDataError);

	const isLoading = loading.first || loading.second || loading.third || loading.fourth;

	const [showModal, setShowModal] = useState(false);

	useEffect(() => {
		if (attrs === undefined && !loading.first) {
			logMessage('address or attrs is undefined - redirect to auswahl');
			navigate('/auswahl');
		}
	}, [attrs, loading, navigate]);

	/* ---------- preference handlers ---------- */

	const toggleSection = (sectionKey) => {
		setLocalPreferences((prev) => ({
			...prev,
			[sectionKey]: {
				...prev[sectionKey],
				checked: !prev[sectionKey].checked,
			},
		}));
	};

	const toggleChild = (sectionKey, childKey) => {
		setLocalPreferences((prev) => ({
			...prev,
			[sectionKey]: {
				...prev[sectionKey],
				children: {
					...prev[sectionKey].children,
					[childKey]: !prev[sectionKey].children[childKey],
				},
			},
		}));
	};

	const toggleExpand = (sectionKey) => {
		setLocalPreferences((prev) => ({
			...prev,
			[sectionKey]: {
				...prev[sectionKey],
				expanded: !prev[sectionKey].expanded,
			},
		}));
	};

	const closeAndPersistPreferences = (source = 'unknown') => {
		//console.log('[Preferences] Persisting preferences on close:', source);
		//console.log(preferences);

		setStoredPreferences(preferences);
		setShowModal(false);
	};

	/* ---------- render helpers ---------- */

	function getLoadingMessage() {
		const step = loading.first ? '0 %' : loading.second ? '33%' : loading.third ? '66%' : '??';
		return (
			<LoadingMessage>
				<>
					Daten werden geladen … <small>{step}</small>
				</>
			</LoadingMessage>
		);
	}

	function getErrorMessage() {
		return (
			<div>
				<h3>Error</h3>
				<p>{error.message}</p>
			</div>
		);
	}

	function getDataForm() {
		if (loading.first || !attrs) return null;

		return (
			<>
				<Row>
					<Col lg={6}>
						<HouseAddressTitle />
					</Col>
					<Col>
						<GeoAdminMapView show />
					</Col>
				</Row>

				<Row>
					<Col lg={6}>
						<InputHeader title="Gebäude-Nutzung" />

						<Row>
							<Col sm={6}>
								<MainFormElement parameterName="_building_constructionYear" />
							</Col>
						</Row>

						<Row>
							<Col sm={6}>
								<MainFormElement parameterName="_building_occupantNumber" />
							</Col>
							<Col>
								<MainFormElement parameterName="_building_livingArea" />
							</Col>
						</Row>

						<InputHeader title="Energie" />

						<MainFormElement
							parameterName="_energySystem_heating0"
							yearParameter="_energySystem_heating0Renovation"
						/>

						<MainFormToggableElement
							parameterName="_solarSystem_installedArea"
							labelOverride="Solarwärme"
						/>

						{/* ---- WARMWASSER (RESTORED, UNCHANGED) ---- */}
						<MainFormElement
							parameterName="_energySystem_hotwater0"
							multiRow={false}
							yearParameter=""
							condition="_solarSystem_installedArea"
							conditionValue={false}
							labelOverride="Warmwasser"
						/>
						<MainFormElement
							parameterName="_energySystem_hotwater1"
							multiRow={false}
							yearParameter=""
							condition="_solarSystem_installedArea"
							conditionValue={true}
							labelOverride="Warmwasser"
						/>

						<MainFormToggableElement
							parameterName="_pvSystem_installedPower"
							labelOverride="Photovoltaik"
						/>
					</Col>

					<Col lg={6}>
						<InputHeader title="Ausstattung" />
						<Row>
							<Col sm={6}>
								<MainFormElement parameterName="_equipment_kitchenRenovation" />
							</Col>
							<Col sm={6}>
								<MainFormElement parameterName="_equipment_washingRenovation" />
							</Col>
						</Row>
					</Col>
				</Row>

				<Row className="pb-5">
					<Col>
						<Stack direction="horizontal" gap={3} className="justify-content-end">
							<Button
								size="sm"
								onClick={() => setShowModal(true)}
								style={{
									width: 200,
									backgroundColor: '#fd7e14', // Bootstrap orange
									borderColor: '#fd7e14',
									color: '#fff',
								}}
							>
								Nutzungs-Präferenzen
							</Button>

							<Link to="/details">
								<Button size="sm" style={{ width: 200 }}>
									Weitere Details erfassen
								</Button>
							</Link>

							<Link to="/situation">
								<Button size="sm" variant="dark" style={{ width: 200 }}>
									Situationsanalyse
								</Button>
							</Link>
						</Stack>
					</Col>
				</Row>
			</>
		);
	}

	let content;
	if (isLoading) content = getLoadingMessage();
	else if (error?.hasError) content = getErrorMessage();
	else content = getDataForm();

	return (
		<>
			<NavBreadcrumb />
			<Container>{content}</Container>

			<Modal
				show={showModal}
				onHide={() => closeAndPersistPreferences('modal-onHide')}
				size="md"
				centered
			>
				<Modal.Header closeButton>
					<Modal.Title>Nutzungs-Präferenzen</Modal.Title>
				</Modal.Header>

				<Modal.Body>
					<Stack gap={3}>
						{preferenceConfig.map((section) => {
							const state = preferences[section.key];

							return (
								<div key={section.key}>
									{/* ===== Section header ===== */}
									<div className="d-flex align-items-center">
										<Form.Check
											type="checkbox"
											checked={state.checked}
											onChange={() => toggleSection(section.key)}
											label={section.label}
										/>

										<button
											type="button"
											className={[
												'btn btn-sm p-0 ms-2 border-0',
												'd-inline-flex align-items-center justify-content-center',
												state.expanded ? 'bg-primary bg-opacity-10' : '',
											].join(' ')}
											onClick={() => toggleExpand(section.key)}
											aria-expanded={state.expanded}
											aria-label={state.expanded ? 'Collapse' : 'Expand'}
											style={{ width: 36, height: 28, borderRadius: 6 }}
										>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="16"
												height="16"
												viewBox="0 0 16 16"
												fill="none"
												className={
													state.expanded
														? 'text-primary'
														: 'text-secondary'
												}
												style={{
													transition: 'transform 120ms ease',
													transform: state.expanded
														? 'rotate(180deg)'
														: 'rotate(0deg)',
												}}
											>
												<path
													d="M3.5 6.5L8 11l4.5-4.5"
													stroke="currentColor"
													strokeWidth="1.5"
													strokeLinecap="round"
													strokeLinejoin="round"
												/>
											</svg>
										</button>
									</div>

									{/* ===== Children ===== */}
									{state.expanded && (
										<div
											className="ms-4 mt-2"
											style={{
												opacity: state.checked ? 1 : 0.5,
												transition: 'opacity 120ms ease',
											}}
										>
											{section.children.map((child) => (
												<div
													key={child}
													className="d-flex align-items-center gap-2"
												>
													<Form.Check
														type="checkbox"
														checked={state.children[child]}
														onChange={() =>
															toggleChild(section.key, child)
														}
														label={child}
													/>

													{qualityTooltipMap[child] && (
														<OverlayTrigger
															placement="right"
															overlay={
																<Tooltip className="tooltip-wide-left">
																	{qualityTooltipMap[child]}
																</Tooltip>
															}
														>
															<span
																className="text-muted d-inline-flex align-items-center"
																style={{ cursor: 'pointer' }}
															>
																<MdOutlineInfo size={18} />
															</span>
														</OverlayTrigger>
													)}
												</div>
											))}
										</div>
									)}
								</div>
							);
						})}
					</Stack>
				</Modal.Body>

				<Modal.Footer>
					<Button
						variant="secondary"
						onClick={() => closeAndPersistPreferences('footer-button')}
					>
						Schließen
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
};
